import React from "react";

import {useMutation} from "@tanstack/react-query";
import cx from "classnames";
import {isNumber} from "lodash";
import moment from "moment";
import {Form} from "react-final-form";

import {DateString} from "@globals/types/utilityTypes";
import {checkValidDateStr} from "@globals/utils/toDateString";

import Button, {ButtonAccent} from "@components/Button";
import LoadingDots from "@components/LoadingDots";
import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton";
import {
  RadioButtonsAlignment,
  RadioButtonsField,
  TextField,
} from "@components/forms";
import ErrorModal from "@components/modals/ErrorModal";

import {submitSurvey} from "../DailyCartService";
import styles from "./MenuSurvey.scss";
import {ERROR_MODAL_CONTENT, MenuSurveyOptions} from "./constants";

export type MenuSurveyProps = {
  className?: string;
  closeSurvey: () => void;
  surveyDate: DateString | null;
};

type MenuSurveyValue = {
  surveyOptions: number;
  feedback: string;
};

export default function MenuSurvey({
  className,
  closeSurvey,
  surveyDate,
}: MenuSurveyProps): JSX.Element {
  const surveyDayFormatted =
    surveyDate && checkValidDateStr(surveyDate)
      ? moment(surveyDate, "YYYY-MM-DD").format("dddd")
      : "that day";

  const mutation = useMutation<void, Error, MenuSurveyValue>({
    mutationFn: (value) => submitSurvey(value.surveyOptions, value.feedback),
    onSuccess: () => {
      setTimeout(() => {
        closeSurvey();
      }, 2000);
    },
    onError: () => {
      closeSurvey();
    },
  });

  const onSubmit = (value: MenuSurveyValue) => {
    mutation.mutate({
      surveyOptions: value.surveyOptions,
      feedback: value.feedback || "",
    });
  };

  if (!surveyDate) {
    return mutation.isError ? (
      <ErrorModal error={mutation.error} defaultContent={ERROR_MODAL_CONTENT} />
    ) : (
      <></>
    );
  }

  return (
    <div className={cx(className, styles.survey)}>
      {mutation.isSuccess && (
        <div className={styles.surveySuccess}>Thank you for your feedback</div>
      )}
      {!mutation.isSuccess && (
        <>
          <TransparentButton
            aria-label="Close"
            onClick={() => closeSurvey()}
            className={styles.closeBtn}
          >
            <NomiconIcon icon="nomicon-dismiss-filled" />
          </TransparentButton>
          <div className={styles.confirmText}>
            Your order has been confirmed.
          </div>
          <div className={styles.question}>
            What did you think of {surveyDayFormatted}'s menu choices? (choose
            one)
          </div>
          <Form
            onSubmit={onSubmit}
            render={({handleSubmit, values}) => {
              const isDisabled = !isNumber(values.surveyOptions);
              return (
                <form className={styles.form} onSubmit={handleSubmit}>
                  <RadioButtonsField
                    required={true}
                    name={"surveyOptions"}
                    options={MenuSurveyOptions}
                    legendText={"Survey Options"}
                    buttonClassName={styles.radioButton}
                    alignment={RadioButtonsAlignment.Vertical}
                  />
                  <label htmlFor="feedback">
                    Tell us more:
                    <TextField
                      inputClassName={styles.textInput}
                      id="feedback"
                      withLayout={true}
                      name="feedback"
                    />
                  </label>
                  <Button
                    className={styles.submit}
                    disabled={isDisabled}
                    withBorder={true}
                    accent={ButtonAccent.AccentOrange}
                    type="submit"
                  >
                    {mutation.isPending ? <LoadingDots /> : "Submit"}
                  </Button>
                </form>
              );
            }}
          />
        </>
      )}
    </div>
  );
}
